import type { CustomFilterProps } from "ag-grid-react";
import { useGridFilter } from "ag-grid-react";
import i18n from "i18next";
import React, { useCallback, useEffect, useState } from "react";
import Checkbox from "src/components/Checkbox";
import k from "src/i18n/keys";
import type { Nullable } from "src/ts-types";
import type { UID } from "src/types";
import {
  FilterContainer,
  FilterItem,
  FilterLabel,
  FilterList,
  SearchInput,
} from "./styles";

interface MultiSelectFilterProps<TData = any> extends CustomFilterProps {
  renderValue: (value: number | string) => React.ReactNode;
  filterType: string;
  data: TData;
  getValueFromData: (
    data: TData,
    uid: UID,
  ) => Nullable<number | string | Array<number | string>>;
  fixedValues?: Array<number | string>;
  getSearchText?: (value: number | string) => string;
}

const MultiSelectFilter = <TData,>({
  model,
  onModelChange,
  renderValue,
  filterType,
  api,
  data,
  getValueFromData,
  fixedValues,
  getSearchText,
}: MultiSelectFilterProps<TData>): React.ReactElement => {
  const [searchText, setSearchText] = useState("");
  const [allValues, setAllValues] = useState<Array<number | string>>([]);

  useEffect(() => {
    if (fixedValues) {
      setAllValues([...fixedValues]);
      return;
    }

    const uniqueValues: Array<number | string> = [];
    const seen = new Set<string | number>();

    api.forEachNode((node) => {
      const uid = node.data?.uid as UID;
      if (!uid) return;

      const values = getValueFromData(data, uid);
      if (values !== null && values !== undefined) {
        if (Array.isArray(values)) {
          values.forEach((value) => {
            if (value !== null && value !== undefined) {
              const key = String(value);
              if (!seen.has(key)) {
                seen.add(key);
                uniqueValues.push(value);
              }
            }
          });
        } else {
          const key = String(values);
          if (!seen.has(key)) {
            seen.add(key);
            uniqueValues.push(values);
          }
        }
      }
    });

    setAllValues(
      uniqueValues.sort((a, b) => String(a).localeCompare(String(b))),
    );
  }, [api, data, getValueFromData, fixedValues]);

  const selectedValues = model ? (Array.isArray(model) ? model : [model]) : [];

  const filteredValues = !searchText
    ? allValues
    : allValues.filter((value) => {
        if (getSearchText) {
          const displayText = getSearchText(value);
          return displayText.toLowerCase().includes(searchText.toLowerCase());
        } else {
          return String(value).toLowerCase().includes(searchText.toLowerCase());
        }
      });

  const doesFilterPass = useCallback(
    (params: any) => {
      if (!selectedValues || selectedValues.length === 0) {
        return true;
      }

      const uid = params.data?.uid as UID;
      if (!uid) return false;

      const rowValue = getValueFromData(data, uid);
      if (rowValue === null || rowValue === undefined) {
        return false;
      }

      if (Array.isArray(rowValue)) {
        return rowValue.some((v) => selectedValues.includes(v));
      } else {
        return selectedValues.includes(rowValue);
      }
    },
    [selectedValues, data, getValueFromData],
  );

  useGridFilter({
    doesFilterPass,
    getModelAsString: () => {
      if (!selectedValues || selectedValues.length === 0) return "";
      return `${filterType}: ${selectedValues.length} selected`;
    },
  });

  const handleToggle = (value: number | string) => {
    const newSelected = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    onModelChange(newSelected.length > 0 ? newSelected : null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <FilterContainer>
      <SearchInput
        type="text"
        placeholder={i18n.t(k.SEARCH)}
        value={searchText}
        onChange={handleSearchChange}
        autoFocus
      />
      <FilterList>
        {filteredValues.map((value) => (
          <FilterItem key={value} onClick={() => handleToggle(value)}>
            <Checkbox
              id={`filter-${filterType}-${value}`}
              checked={selectedValues.includes(value)}
            />
            <FilterLabel>{renderValue(value)}</FilterLabel>
          </FilterItem>
        ))}
      </FilterList>
    </FilterContainer>
  );
};

export default MultiSelectFilter;
